import { Component, h, Prop, State, Element } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';
import { AafpFilteredSearchResult, AafpFilteredSearchTemplateName, templates } from './result-templates';

// declaration merging to declare support for methods present in modern browsers
declare global {
  interface FormData {
    entries(): IterableIterator<[string, string | File]>;
    [Symbol.iterator](): IterableIterator<string | File>;
  }
}

let uid = 0;

/**
 * COMPNAME is for allowing the user to search and filter data returned from an endpoint, rendering each result with a template function.
 */
@Component({
  tag: 'aafp-filtered-search',
  shadow: false,
})
export class AafpFilteredSearch {
  /**
   * For the endpoint that returns results (as JSON)
   */
  @Prop() resultsEndpoint!: string;
  /**
   * For the name of a pre-defined JSX template for each search result. The "props" for the template you use should match up with the structure of the data you expect to have returned from the endpoint.
   */
  @Prop() resultTemplateName!: AafpFilteredSearchTemplateName;
  /**
   * For the number of results displayed per page
   */
  @Prop() resultsPerPage!: number;

  @Element() element: HTMLElement;

  id: string;
  resultsEl: HTMLElement;
  form: HTMLFormElement | null = null;
  submitBtn: HTMLButtonElement | null = null;

  @State() isFetching: boolean = false;
  @State() error: boolean = false;
  @State() currentPage: number = 0;
  @State() results: AafpFilteredSearchResult[] = [];

  componentWillLoad() {
    this.id = this.element.getAttribute('id') || 'aafp-filtered-search-' + uid++;

    this.form = this.element.querySelector('form');

    if (this.form) {
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.form.addEventListener('submit', this.onSubmit, false);

      this.fetchResults();
    }
  }

  private onSubmit = (e: Event) => {
    e.preventDefault();
    this.fetchResults();
  };

  private serializeFormData = (): string => {
    const formData = new FormData(this.form as HTMLFormElement);

    // delete empty form fields so they aren't serialized
    let keysToDelete = [];

    for (let [name, value] of formData.entries()) {
      if (!value) {
        keysToDelete.push(name);
      }
    }

    keysToDelete.forEach((key) => formData.delete(key));

    const params = new URLSearchParams(formData as any).toString();

    return params ? '?' + params : '';
  };

  private fetchResults = () => {
    if (this.isFetching) {
      return;
    }

    this.isFetching = true;
    this.error = false;

    if (this.submitBtn) {
      this.submitBtn.disabled = true;
    }

    const serialized = this.serializeFormData();

    fetch(`${this.resultsEndpoint}${serialized}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }

        return response;
      })
      .then((response) => response.json())
      .then((results: AafpFilteredSearchResult[]) => {
        this.currentPage = 0;
        this.results = results;
        this.isFetching = false;

        if (this.submitBtn) {
          this.submitBtn.disabled = false;
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);

        this.currentPage = 0;
        this.results = [];
        this.isFetching = false;
        this.error = true;

        if (this.submitBtn) {
          this.submitBtn.disabled = false;
        }
      });
  };

  private setCurrentPage = (pageIndex: number) => {
    // pagination is handled on the FE
    this.currentPage = pageIndex;

    if (this.resultsEl.getBoundingClientRect().top < 0) {
      this.element.scrollIntoView(true);
    }
  };

  render() {
    const start = this.currentPage * this.resultsPerPage;
    const end = start + this.resultsPerPage;
    const totalPages = Math.ceil(this.results.length / this.resultsPerPage);
    const results = this.results.slice(start, end);
    const ResultTemplate = templates[this.resultTemplateName];

    return (
      <div class="adl-filtered-search" aria-busy={this.isFetching ? 'true' : 'false'}>
        <div class="adl-filtered-search__form adl-space__sm__m-b-lg">
          <slot name="form"></slot>
        </div>
        <div class="adl-filtered-search__results" ref={(el) => (this.resultsEl = el as HTMLElement)}>
          {this.isFetching && <aafp-spinner-inline actionId={`${this.id}__spinner`} size="lg" />}
          {results.length > 0 && (
            <ul class="adl-filtered-search__results-list adl-space__sm__m-b-lg">
              {results.map((result, index) => (
                <li class="adl-space__sm__m-b-md" key={index}>
                  <ResultTemplate {...result} />
                </li>
              ))}
            </ul>
          )}
        </div>
        <div class="adl-filtered-search__footer">
          {this.error && (
            <p class="color-error-red adl-filtered-search__result-count adl-space__sm-m-b-md">Something went wrong</p>
          )}
          {!this.error && (
            <p class="adl-filtered-search__result-count adl-space__sm__m-b-md">
              {this.results.length > 0
                ? `Showing ${start + 1} to ${end} of ${this.results.length} entries`
                : `No results found`}
            </p>
          )}
          {!this.error && this.results.length > 0 && (
            <div class="adl-filtered-search__pagination adl-space__sm__m-b-md">
              <button
                type="button"
                class="adl-filtered-search__pagination-arrow"
                disabled={this.currentPage === 0}
                onClick={() => this.setCurrentPage(this.currentPage - 1)}
              >
                <i class="material-icons">chevron_left</i>
              </button>
              <ul class="adl-filtered-search__pagination-list">
                {Array(totalPages)
                  .fill(undefined)
                  .map((_, pageIndex) => (
                    <li class="adl-filtered-search__pagination-list-item">
                      <button
                        type="button"
                        class={getClassListString(
                          { 'adl-filtered-search__pagination-link--active': this.currentPage === pageIndex },
                          'adl-filtered-search__pagination-link',
                        )}
                        onClick={() => this.setCurrentPage(pageIndex)}
                      >
                        {pageIndex + 1}
                      </button>
                    </li>
                  ))}
              </ul>
              <button
                type="button"
                class="adl-filtered-search__pagination-arrow"
                disabled={this.currentPage === totalPages - 1}
                onClick={() => this.setCurrentPage(this.currentPage + 1)}
              >
                <i class="material-icons">chevron_right</i>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
}
